"use strict";
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { cloneDeep, difference, times } from "../../_utils/lodash-lite.js";
import { cloneForStorage } from "../helpers.js";
import { dbPromise, getDatabase } from "../databaseLifecycle.ts";
import {
  accountsCache,
  notificationsCache,
  setInCache,
  statusesCache
} from "../cache.js";
import { scheduleCleanup } from "../cleanup.js";
import {
  ACCOUNTS_STORE,
  NOTIFICATION_TIMELINES_STORE,
  NOTIFICATIONS_STORE,
  STATUS_TIMELINES_STORE,
  STATUSES_STORE,
  THREADS_STORE
} from "../constants.js";
import {
  createThreadId,
  createThreadKeyRange,
  createTimelineId
} from "../keys.js";
import { cacheStatus } from "./cacheStatus.js";
import { rehydrated } from "../../_actions/rehydrateStatusOrNotification.js";
export function putStatus(statusesStore, status) {
  try {
    statusesStore.put(cloneForStorage(status));
  } catch (e) {
    console.warn(e);
  }
}
function putAccount(accountsStore, account) {
  try {
    if (account.acct) {
      accountsStore.put(cloneForStorage(account));
    }
  } catch (e) {
    console.warn(e);
  }
}
function putNotification(notificationsStore, notification) {
  try {
    notificationsStore.put(cloneForStorage(notification));
  } catch (e) {
    console.warn(e);
  }
}
export function storeStatus(statusesStore, accountsStore, status) {
  if (status[rehydrated]) {
    console.warn(new Error("attempt to store hydrated status in db"));
    return;
  }
  putStatus(statusesStore, status);
  putAccount(accountsStore, status.account);
  if (status.reblog) {
    putStatus(statusesStore, status.reblog);
    putAccount(accountsStore, status.reblog.account);
  }
}
function storeNotification(notificationsStore, statusesStore, accountsStore, notification) {
  if (notification.status) {
    storeStatus(statusesStore, accountsStore, notification.status);
  }
  putAccount(accountsStore, notification.account);
  putNotification(notificationsStore, notification);
}
function insertTimelineNotifications(instanceName, timeline, notifications) {
  return __async(this, null, function* () {
    for (const notification of notifications) {
      setInCache(notificationsCache, instanceName, notification.id, notification);
      setInCache(
        accountsCache,
        instanceName,
        notification.account.id,
        notification.account
      );
      if (notification.status) {
        setInCache(
          statusesCache,
          instanceName,
          notification.status.id,
          cloneDeep(notification.status)
        );
      }
    }
    const db = yield getDatabase(instanceName);
    yield dbPromise(
      db,
      [
        NOTIFICATION_TIMELINES_STORE,
        NOTIFICATIONS_STORE,
        ACCOUNTS_STORE,
        STATUSES_STORE
      ],
      "readwrite",
      (stores) => {
        const [timelineStore, notificationsStore, accountsStore, statusesStore] = stores;
        for (const notification of notifications) {
          storeNotification(
            notificationsStore,
            statusesStore,
            accountsStore,
            notification
          );
          timelineStore.put(
            notification.id,
            createTimelineId(timeline, notification.id)
          );
        }
      }
    );
  });
}
function insertTimelineStatuses(instanceName, timeline, statuses) {
  return __async(this, null, function* () {
    for (const status of statuses) {
      cacheStatus(status, instanceName);
    }
    const db = yield getDatabase(instanceName);
    yield dbPromise(
      db,
      [STATUS_TIMELINES_STORE, STATUSES_STORE, ACCOUNTS_STORE],
      "readwrite",
      (stores) => {
        const [timelineStore, statusesStore, accountsStore] = stores;
        for (const status of statuses) {
          storeStatus(statusesStore, accountsStore, status);
          timelineStore.put(status.id, createTimelineId(timeline, status.id));
        }
      }
    );
  });
}
function insertStatusThread(instanceName, statusId, statuses) {
  return __async(this, null, function* () {
    for (const status of statuses) {
      cacheStatus(status, instanceName);
    }
    const db = yield getDatabase(instanceName);
    yield dbPromise(
      db,
      [THREADS_STORE, STATUSES_STORE, ACCOUNTS_STORE],
      "readwrite",
      (stores) => {
        const [threadsStore, statusesStore, accountsStore] = stores;
        const req = threadsStore.getAllKeys(createThreadKeyRange(statusId));
        req.onsuccess = () => {
          const existingKeys = req.result;
          const newKeys = times(
            statuses.length,
            (i) => createThreadId(statusId, i)
          );
          const keysToDelete = difference(existingKeys, newKeys);
          for (const key of keysToDelete) {
            threadsStore.delete(key);
          }
        };
        statuses.forEach((otherStatus, i) => {
          storeStatus(statusesStore, accountsStore, otherStatus);
          threadsStore.put(otherStatus.id, createThreadId(statusId, i));
        });
      }
    );
  });
}
export function insertTimelineItems(instanceName, timeline, timelineItems) {
  return __async(this, null, function* () {
    console.log(
      "insertTimelineItems",
      instanceName,
      timeline,
      timelineItems.length,
      "items"
    );
    scheduleCleanup();
    if (timeline === "notifications" || timeline === "notifications/mentions") {
      return insertTimelineNotifications(instanceName, timeline, timelineItems);
    } else if (timeline.startsWith("status/")) {
      const statusId = timeline.split("/").slice(-1)[0];
      return insertStatusThread(instanceName, statusId, timelineItems);
    } else {
      return insertTimelineStatuses(instanceName, timeline, timelineItems);
    }
  });
}
export function insertStatus(instanceName, status) {
  return __async(this, null, function* () {
    cacheStatus(status, instanceName);
    const db = yield getDatabase(instanceName);
    yield dbPromise(
      db,
      [STATUSES_STORE, ACCOUNTS_STORE],
      "readwrite",
      ([statusesStore, accountsStore]) => {
        storeStatus(statusesStore, accountsStore, status);
      }
    );
  });
}
